import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Post } from "../../api/apiUtils";
import { SofView } from "../../models";
import {
  mergeStyleSets,
  PrimaryButton,
  DefaultButton,
  MessageBar,
  MessageBarType,
} from "office-ui-fabric-react";
import { mergeStyles, FontIcon, TextField } from "@fluentui/react";
import { State } from "../../store";
import { useHistory } from "react-router";
import * as ActivityLogStore from "../../store/ActivityLogsStore";
import * as GeneralStore from "../../store/GeneralStore";
import * as LineHandlingStore from "../../store/LineHandlingStore";
import * as SofViewStore from "../../store/SofViewStore";

type Props = GeneralStore.GeneralState &
  LineHandlingStore.LineHandlingState &
  typeof ActivityLogStore.actionCreators &
  typeof LineHandlingStore.actionCreators &
  typeof SofViewStore.actionCreators;

const buttonBase = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0",
    },
  },
});
const buttonIcon = mergeStyles({
  padding: "9px",
  height: 38,
  width: 38,
  justifyContent: "center",
  border: "1px solid var(--grey-200)",
  display: "flex",
  alignItems: "center",
  color: "#4c4c4c",
  background: "var(--grey-050)",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0",
    },
  },
});
const classes = mergeStyleSets({
  header: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 24,
    color: "#00447E",
    textAlign: "left",
  },
  body: {
    textAlign: "left",
    margin: "18px 0 38px 0",
  },
  footer: {
    textAlign: "right",
  },
  button: [
    buttonBase,
    {
      padding: "10px 16.5px",
      height: "auto",
    },
  ],
  modalButton: [
    buttonBase,
    {
      padding: "9px 20px",
    },
  ],
  primaryButton: [
    buttonBase,
    {
      backgroundColor: "#009FDF",
      color: "#FFFFFF",
      padding: "9px 20px",
    },
  ],
});

const RejectLineHandlingModal: React.FC<Props> = (props: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const [lineHandlingNo, setLineHandlingNo] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [reason, setReason] = useState<string>();
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [isRejected, setIsRejected] = useState(false);
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState<string>();

  const rejectLineHandlingClick = () => {
    setShowModal(true);
  };

  const rejectLineHandling = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      e.preventDefault();
      setIsLoading(true);
      if (lineHandlingNo === undefined) return;
      const response = await Post(
        `api/linehandling/reject/${lineHandlingNo}`,
        JSON.stringify({ reason })
      );
      setShowModal(false);
      setIsLoading(false);

      props.getActivityLogs(lineHandlingNo, () => {
        props.getLineHandling(lineHandlingNo, "", false, false, () => {
          let previousPage = `?lineHandling=${lineHandlingNo}`;
          props.setSofView(SofView.LineHandling);
          if (history.location.search !== previousPage) {
            history.push(`/sof${previousPage}`);
          }
        });
      });
    } catch {
      setErrorMessage("Unable to save changes. Please try to reconnect.");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (props.lineHandling.result) {
      setLineHandlingNo(props.lineHandling.result.lineHandlingNo);

      setIsRejected(props.lineHandling.result.isRejected);
    }
  }, [props.lineHandling]);

  return (
    <>
      {props.isMarineRejectLineHandlingAllowed && (
        <button
          id="btn-undo"
          className={buttonIcon}
          onClick={rejectLineHandlingClick}
          disabled={isRejected}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.56 17.2142L15.7916 15.4467L17.56 13.6792L16.3808 12.5L14.6133 14.2683L12.8458 12.5L11.6666 13.6792L13.435 15.4467L11.6666 17.2142L12.8458 18.3933L14.6133 16.625L16.3808 18.3933L17.56 17.2142Z" fill="#353B3B"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M4.16667 3.33335H12.5V6.66669H15.8333V10.8334H17.5L17.5 5.83335L13.3333 1.66669H3.3325C2.8725 1.66669 2.5 2.03669 2.5 2.49335V17.5067C2.50175 17.7255 2.58951 17.9349 2.74435 18.0896C2.89918 18.2443 3.10865 18.3318 3.3275 18.3334H10.8333V16.6667H4.16667V3.33335ZM17.5 17.4942C17.5 17.4981 17.5 17.5021 17.5 17.506L17.5 17.4942Z" fill="#353B3B"/>
          </svg>
        </button>
      )}
      {showModal && (
        <div className="modal-bg">
          <div className="modal-right" style={{ width: 400 }}>
            <div className={classes.header}>
              Reject {props.lineHandling.result?.vesselType}
            </div>
            <div className={classes.body}>
              <span>
                You are about to reject this line handling. Are you sure you
                want to proceed?
              </span>
            </div>
            <div className={classes.footer}>
              <PrimaryButton
                type="button"
                className="btn-prim"
                id="btn-submit-undo"
                onClick={(event: any) => {
                  setShowReasonModal(true);
                  setShowModal(false);
                }}
                disabled={isLoading}
              >
                Proceed
              </PrimaryButton>
              &nbsp;
              <DefaultButton
                id="btn-cancel-undo"
                type="button"
                className="btn-def"
                onClick={() => setShowModal(false)}
                disabled={isLoading}
              >
                Cancel
              </DefaultButton>
            </div>
          </div>
        </div>
      )}
      {showReasonModal && (
        <div className="modal-bg">
          <div className="modal-right" style={{ width: 400 }}>
            <form>
              <div className={classes.header}>Reject Movement</div>
              <div className={classes.body}>
                <span>Please specify a reason for rejection</span>
                <TextField
                  multiline
                  disabled={isLoading}
                  onChange={(_, value?: string) => setReason(value)}
                  required
                  value={reason}
                  maxLength={1000}
                ></TextField>

                {errorMessage && (
                  <div style={{ marginTop: "10px" }}>
                    <MessageBar
                      messageBarType={MessageBarType.error}
                      onDismiss={() => setErrorMessage(undefined)}
                    >
                      {errorMessage}
                    </MessageBar>
                  </div>
                )}
              </div>
              <div className={classes.footer}>
                <PrimaryButton
                  id="btn-submit-undo"
                  onClick={rejectLineHandling}
                  type="button"
                  className="btn-prim"
                  disabled={
                    isLoading || reason === undefined || reason.trim() === ""
                  }
                >
                  Confirm
                </PrimaryButton>
                &nbsp;
                <DefaultButton
                  id="btn-cancel-undo"
                  type="button"
                  onClick={() => setShowReasonModal(false)}
                  disabled={isLoading}
                  className="btn-def"
                >
                  Cancel
                </DefaultButton>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
export default connect(
  (state: State) => ({
    ...state.general,
    ...state.lineHandling,
  }),
  {
    ...ActivityLogStore.actionCreators,
    ...LineHandlingStore.actionCreators,
    ...SofViewStore.actionCreators,
  }
)(RejectLineHandlingModal as any);
